/**
 * SOLOMON — useCostMap
 *
 * Fetches GET /api/system/batchdata-cost-map once on mount, caches in
 * module-level state so navigation away and back doesn't re-fetch.
 *
 * Returns: { costMap, threshold, isLoaded, error }
 *
 * Graceful degradation: on fetch failure, costMap is null and components
 * handle null by returning null (no subtitle, no dialog gate).
 *
 * NOTE: authFetch comes from useAuth() (Solomon's AuthContext), not a
 * separate api/client module. API_BASE follows the project-wide convention
 * (import.meta.env.VITE_API_URL || 'http://localhost:3001').
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Module-level cache — survives component unmount/remount
let _cached = null;
let _cachedError = null;
let _inFlight = null;

async function fetchOnce(authFetch) {
  if (_cached || _cachedError) return;
  if (_inFlight) return _inFlight;

  _inFlight = (async () => {
    try {
      const res = await authFetch(`${API_BASE}/api/system/batchdata-cost-map`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success || !json.data) throw new Error(json.error || 'malformed response');
      _cached = json.data;
    } catch (err) {
      console.error('[useCostMap] fetch failed:', err);
      _cachedError = err;
    } finally {
      _inFlight = null;
    }
  })();

  return _inFlight;
}

export function useCostMap() {
  const [, setTick] = useState(0);
  const { authFetch } = useAuth();

  useEffect(() => {
    let mounted = true;
    fetchOnce(authFetch).then(() => {
      if (mounted) setTick(t => t + 1);
    });
    return () => { mounted = false; };
  }, [authFetch]);

  return {
    costMap: _cached,
    threshold: _cached?.threshold_usd ?? null,
    isLoaded: _cached !== null || _cachedError !== null,
    error: _cachedError,
  };
}
